import {
  FileInput,
  ImageList,
  Modal,
  SwitchInput,
  TextInput,
} from 'melp-design/components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Delete } from '@mui/icons-material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { Stack } from '@mui/material';
import { useAdminCreateSupplier } from 'store/admin-suppliers';
import { ROUTES } from 'config';
import { useUploadFile } from 'store/files';
import { routerHistory } from 'utils/router-history';
import { SupplierStatus } from 'store/suppliers';

interface Values {
  name: string;
  status: SupplierStatus;
  logo: string | null;
  description: string;
  additionalInfo: string;
  termsAndConditionsLink: string;
}

const INITIAL_VALUES: Values = {
  name: '',
  status: 'inactive',
  logo: null,
  description: '',
  additionalInfo: '',
  termsAndConditionsLink: '',
};

interface Props extends ModalProps {}

export const AdminCreateSupplierModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit, watch } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { mutate: createSupplier, isLoading: isCreating } =
    useAdminCreateSupplier();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const onSubmit: SubmitHandler<Values> = (values) => {
    createSupplier(
      { data: values },
      {
        onSuccess: ({ data }) => {
          predefinedToasts.success.added();
          closeModal();
          routerHistory.push(
            ROUTES.admin.suppliers.details.replace(':id', data.id),
          );
        },
      },
    );
  };

  const [logo] = watch(['logo']);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('marketplaceSuppliers.addSupplier')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.create'),
            type: 'submit',
            disabled: isCreating,
          },
        ]}
      >
        <Stack spacing={2}>
          <Controller
            name="status"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SwitchInput
                label={t('status.active')}
                name={name}
                value={value === 'active'}
                onChange={(checked) =>
                  onChange(checked ? 'active' : 'inactive')
                }
                ref={ref}
              />
            )}
          />

          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextInput
                label={t('marketplaceSuppliers.name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="logo"
            control={control}
            render={({ field: { name, onChange, ref } }) => {
              return logo ? (
                <ImageList
                  items={[
                    {
                      imageId: logo,
                      actions: [
                        {
                          icon: Delete,
                          variant: 'neutral-outline',
                          onClick: () => onChange(''),
                        },
                      ],
                    },
                  ]}
                />
              ) : (
                <FileInput
                  name={name}
                  onChange={([file]) => {
                    if (file) {
                      uploadFile(
                        { file },
                        {
                          onSuccess: ({ data }) => {
                            onChange(data.id);
                          },
                        },
                      );
                    } else {
                      onChange('');
                    }
                  }}
                  disabled={isUploading}
                  maxFiles={1}
                  previewSelectedFiles={false}
                  accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextInput
                label={t('employees.details')}
                name={name}
                value={value}
                onChange={onChange}
                rows={3}
                ref={ref}
              />
            )}
          />

          <Controller
            name="additionalInfo"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextInput
                label={t('marketplaceSuppliers.additionalInfo')}
                name={name}
                value={value}
                onChange={onChange}
                rows={3}
                ref={ref}
              />
            )}
          />

          <Controller
            name="termsAndConditionsLink"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextInput
                label={t('marketplaceSuppliers.termsAndConditionsLink')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
